import { useRef } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Download, FileText } from 'lucide-react';

// Datos de ejemplo para el gráfico de barras
const barData = [
  { mes: 'Hitzell Ordoñez', ventas: 4000 },
  { mes: 'Diana Margarita Sánchez', ventas: 3000 },
  { mes: 'Eduardo Jimenez', ventas: 2000 },
  { mes: 'Elsa Ivette Rodríguez', ventas: 2780 },
  { mes: 'Francisco Cabañas', ventas: 1890 },
  { mes: 'Jesús Arturo Ayala', ventas: 2390 },
  { mes: 'Hitzell Ordñez', ventas: 4000 },
  { mes: 'Diana Margita Sánchez', ventas: 3000 },
  { mes: 'Eduardo Jimnez', ventas: 2000 },
  { mes: 'Elsa Ivette Roríguez', ventas: 2780 },
  { mes: 'Francisco Cabañas', ventas: 1890 },
  { mes: 'Jesús Arturo Aala', ventas: 2390 }
];

// Datos de ejemplo para el gráfico de pie
const pieData = [
  { name: 'Inmuebles', value: 35, color: '#3b82f6' },
  { name: 'Medios', value: 25, color: '#ef4444' },
  { name: 'Cartera Crédito y Captación', value: 20, color: '#10b981' },
  { name: 'Monitor', value: 15, color: '#f59e0b' },
  { name: 'Seguridad', value: 5, color: '#8b5cf6' }
];

const ChartsDashboard = () => {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const barChartRef = useRef<HTMLDivElement>(null);
  const pieChartRef = useRef<HTMLDivElement>(null);

  const exportToPDF = async () => {
    if (!dashboardRef.current || !barChartRef.current || !pieChartRef.current) return;

    try {
      // Importar las librerías dinámicamente
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      // Crear el documento PDF
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Función para añadir imagen al PDF
      const addImageToPDF = (canvas: HTMLCanvasElement, x: number, y: number, width: number, height: number) => {
        const imgData = canvas.toDataURL('image/png');
        doc.addImage(imgData, 'PNG', x, y, width, height);
      };

      // Configuración para html2canvas
      const canvasOptions = {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        ignoreElements: (element: Element) => {
          // Ignorar elementos que puedan causar problemas con oklch
          return element.classList?.contains('ignore-in-pdf') || false;
        }
      };

      // Añadir título y fecha
      doc.setFontSize(24);
      doc.setTextColor(31, 41, 55); // text-gray-800
      doc.text('Dashboard de Análisis', 20, 25);

      doc.setFontSize(12);
      doc.setTextColor(107, 114, 128); // text-gray-500
      doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`, 20, 35);

      // Capturar gráfico de barras
      const barCanvas = await html2canvas(barChartRef.current, canvasOptions);
      const barImgWidth = pageWidth - 40;
      const barImgHeight = (barCanvas.height * barImgWidth) / barCanvas.width;

      doc.setFontSize(16);
      doc.setTextColor(31, 41, 55);
      doc.text('1. Ventas vs Gastos Mensuales', 20, 50);

      addImageToPDF(barCanvas, 20, 55, barImgWidth, Math.min(barImgHeight, 80));

      // Añadir nueva página si es necesario
      let currentY = 55 + Math.min(barImgHeight, 80) + 20;
      if (currentY > pageHeight - 100) {
        doc.addPage();
        currentY = 20;
      }

      // Capturar gráfico de pie
      const pieCanvas = await html2canvas(pieChartRef.current, canvasOptions);
      const pieImgWidth = pageWidth - 40;
      const pieImgHeight = (pieCanvas.height * pieImgWidth) / pieCanvas.width;

      doc.setFontSize(16);
      doc.setTextColor(31, 41, 55);
      doc.text('2. Distribución por Categorías', 20, currentY);

      currentY += 10;
      addImageToPDF(pieCanvas, 20, currentY, pieImgWidth, Math.min(pieImgHeight, 100));

      // Añadir resumen de datos
      currentY += Math.min(pieImgHeight, 100) + 20;
      if (currentY > pageHeight - 80) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(16);
      doc.text('3. Resumen de Datos', 20, currentY);
      currentY += 15;

      // Datos del gráfico de barras
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55);
      doc.text('Datos Mensuales:', 20, currentY);
      currentY += 8;

      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      barData.forEach((item) => {
        doc.text(`• ${item.mes}: Ventas ${item.ventas.toLocaleString()}`, 25, currentY);
        currentY += 6;
      });

      currentY += 10;
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55);
      doc.text('Distribución por Categorías:', 20, currentY);
      currentY += 8;

      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      pieData.forEach((item) => {
        doc.text(`• ${item.name}: ${item.value}%`, 25, currentY);
        currentY += 6;
      });

      // Totales
      currentY += 10;
      const totalVentas = barData.reduce((sum, item) => sum + item.ventas, 0);
      const ganancia = totalVentas

      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55);
      doc.text('Totales:', 20, currentY);
      currentY += 8;

      doc.setFontSize(10);
      doc.setTextColor(59, 130, 246); // blue-500
      doc.text(`• Total Ventas: ${totalVentas.toLocaleString()}`, 25, currentY);
      currentY += 6;


      if (ganancia >= 0) {
        doc.setTextColor(16, 185, 129); // green-500
      } else {
        doc.setTextColor(239, 68, 68); // red-500
      }
      doc.text(`• ${ganancia >= 0 ? 'Ganancia' : 'Pérdida'}: ${Math.abs(ganancia).toLocaleString()}`, 25, currentY);

      // Pie de página
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(156, 163, 175); // gray-400
        doc.text(`Página ${i} de ${pageCount}`, pageWidth - 30, pageHeight - 10);
        doc.text('Generado automáticamente', 20, pageHeight - 10);
      }

      // Guardar el PDF
      doc.save(`dashboard-reporte-${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('Error al exportar PDF:', error);
      alert('Error al exportar PDF. Asegúrate de tener instaladas las dependencias: npm install html2canvas jspdf');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard de Análisis</h1>
              <p className="text-gray-600 mt-2">Reporte generado el {new Date().toLocaleDateString()}</p>
            </div>
            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Download className="w-4 h-4" />
              Exportar PDF
            </button>
          </div>
        </div>

        {/* Contenido para exportar */}
        <div ref={dashboardRef} className="space-y-6">
          {/* Gráfico de Barras */}
          <div ref={barChartRef} className="bg-white rounded-lg shadow-sm p-6" style={{ backgroundColor: '#ffffff' }}>
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5" style={{ color: '#3b82f6' }} />
              <h2 className="text-xl font-semibold" style={{ color: '#111827' }}>Ingresos por personal quien autoriza</h2>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="mes"
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  {/* <Legend /> */}
                  <Bar dataKey="ventas" fill="#3b82f6" name="Ventas" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráfico de Pie */}
          <div ref={pieChartRef} className="bg-white rounded-lg shadow-sm p-6" style={{ backgroundColor: '#ffffff' }}>
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5" style={{ color: '#10b981' }} />
              <h2 className="text-xl font-semibold" style={{ color: '#111827' }}>Visitas por Área</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value}%`, 'Porcentaje']}
                      contentStyle={{
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Leyenda personalizada */}
              <div className="flex flex-col justify-center space-y-3">
                {pieData.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="font-medium" style={{ color: '#374151' }}>{item.name}</span>
                    <span className="ml-auto" style={{ color: '#6b7280' }}>{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabla resumen */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen de Datos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Totales Mensuales</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total Ventas:</span>
                    <span className="font-semibold text-blue-600">
                      ${barData.reduce((sum, item) => sum + item.ventas, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Categoría Principal</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Mayor categoría:</span>
                    <span className="font-semibold text-green-600">
                      {pieData.reduce((max, item) => item.value > max.value ? item : max).name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Porcentaje:</span>
                    <span className="font-semibold">
                      {pieData.reduce((max, item) => item.value > max.value ? item : max).value}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartsDashboard;